// Builds and validates the canonical content registry from explicit imports.
import { ASSETS } from '../../configs/assets';
import { gameLibrary } from '../../configs/gameLibrary';
import { collections } from '../collections';
import { documents } from '../documents';
const assertUnique = (items, getKey, label) => {
    const seen = new Set();
    for (const item of items) {
        const key = getKey(item);
        if (seen.has(key)) {
            throw new Error(`Duplicate ${label}: ${key}`);
        }
        seen.add(key);
    }
};
const visitRichText = (nodes, visitor) => {
    if (!nodes)
        return;
    for (const node of nodes) {
        visitor(node);
        if ('children' in node) {
            visitRichText(node.children, visitor);
        }
    }
};
const visitBlock = (block, visitor) => {
    switch (block.type) {
        case 'paragraph':
        case 'quote':
        case 'callout':
            visitor(block.content);
            if (block.type === 'quote') {
                visitor(block.attribution ?? []);
            }
            return;
        case 'list':
            block.items.forEach(visitor);
            return;
        case 'image':
            visitor(block.caption ?? []);
            return;
        case 'table':
            block.columns.forEach(visitor);
            block.rows.forEach((row) => row.forEach(visitor));
            return;
        case 'facts':
            block.items.forEach((item) => {
                visitor(item.label);
                visitor(item.value);
            });
            return;
    }
};
const validateSectionTree = (sections, ownerId, allSectionIds = new Set()) => {
    if (sections.length === 0) {
        throw new Error(`Content item has no sections: ${ownerId}`);
    }
    for (const section of sections) {
        if (allSectionIds.has(section.id)) {
            throw new Error(`Duplicate section id in ${ownerId}: ${section.id}`);
        }
        allSectionIds.add(section.id);
        if (section.sections) {
            validateSectionTree(section.sections, ownerId, allSectionIds);
        }
    }
};
const collectWidgets = (sections) => sections.flatMap((section) => [
    ...(section.widgets ?? []),
    ...(section.sections ? collectWidgets(section.sections) : []),
]);
const collectBlocks = (sections) => sections.flatMap((section) => [
    ...(section.blocks ?? []),
    ...(section.sections ? collectBlocks(section.sections) : []),
]);
const collectSections = (sections) => sections.flatMap((section) => [
    section,
    ...(section.sections ? collectSections(section.sections) : []),
]);
const validateAsset = (assetName, ownerId) => {
    if (assetName && !(assetName in ASSETS)) {
        throw new Error(`Missing asset reference in ${ownerId}: ${assetName}`);
    }
};
const validateRichTextReferences = (ownerId, nodes, registry) => {
    visitRichText(nodes, (node) => {
        if (node.type === 'docLink' &&
            !registry.documentsById.has(node.documentId)) {
            throw new Error(`Broken docLink in ${ownerId}: ${node.documentId}`);
        }
        if (node.type === 'collectionLink' &&
            !registry.collectionsById.has(node.collectionId)) {
            throw new Error(`Broken collectionLink in ${ownerId}: ${node.collectionId}`);
        }
    });
};
const validateWidgets = (ownerId, widgets, registry) => {
    const gameIds = new Set(gameLibrary.map((game) => game.gameCore.gameId));
    for (const widget of widgets) {
        if ((widget.type === 'clipScroller' || widget.type === 'songTable') &&
            !gameIds.has(widget.gameId)) {
            throw new Error(`Broken game widget in ${ownerId}: ${widget.gameId}`);
        }
        if (widget.type === 'documentList') {
            const source = widget.source;
            if (source.type === 'collection' &&
                !registry.collectionsById.has(source.collectionId)) {
                throw new Error(`Broken documentList collection in ${ownerId}: ${source.collectionId}`);
            }
            if (source.type === 'query' &&
                source.relatedToDocumentId &&
                !registry.documentsById.has(source.relatedToDocumentId)) {
                throw new Error(`Broken documentList related document in ${ownerId}: ${source.relatedToDocumentId}`);
            }
        }
        if (widget.type === 'relatedDocuments') {
            for (const documentId of widget.documentIds ?? []) {
                if (!registry.documentsById.has(documentId)) {
                    throw new Error(`Broken related document in ${ownerId}: ${documentId}`);
                }
            }
        }
        if (widget.type === 'linkCardGrid') {
            for (const item of widget.items) {
                if ('collectionId' in item &&
                    !registry.collectionsById.has(item.collectionId)) {
                    throw new Error(`Broken linkCardGrid collection in ${ownerId}: ${item.collectionId}`);
                }
            }
        }
    }
};
const validateRegistry = (registry) => {
    assertUnique(registry.documents, (document) => document.id, 'document id');
    assertUnique(registry.collections, (collection) => collection.id, 'collection id');
    assertUnique([...registry.documents, ...registry.collections], (item) => item.route, 'content route');
    for (const document of registry.documents) {
        validateSectionTree(document.sections, document.id);
        validateAsset(document.seo.imageAsset, document.id);
        validateAsset(document.hero?.imageAsset, document.id);
        validateRichTextReferences(document.id, document.hero?.eyebrow, registry);
        validateRichTextReferences(document.id, document.hero?.title, registry);
        validateRichTextReferences(document.id, document.hero?.description, registry);
        for (const section of collectSections(document.sections)) {
            validateRichTextReferences(document.id, section.title, registry);
            validateRichTextReferences(document.id, section.description, registry);
        }
        validateWidgets(document.id, collectWidgets(document.sections), registry);
        for (const block of collectBlocks(document.sections)) {
            if (block.type === 'image') {
                validateAsset(block.imageAsset, document.id);
            }
            visitBlock(block, (nodes) => validateRichTextReferences(document.id, nodes, registry));
        }
    }
    for (const collection of registry.collections) {
        if (collection.sections.length === 0) {
            throw new Error(`Content collection has no sections: ${collection.id}`);
        }
        validateAsset(collection.seo.imageAsset, collection.id);
        validateAsset(collection.hero?.imageAsset, collection.id);
        validateRichTextReferences(collection.id, collection.hero?.eyebrow, registry);
        validateRichTextReferences(collection.id, collection.hero?.title, registry);
        validateRichTextReferences(collection.id, collection.hero?.description, registry);
        for (const section of collection.sections) {
            validateRichTextReferences(collection.id, section.title, registry);
            validateRichTextReferences(collection.id, section.description, registry);
            validateWidgets(collection.id, section.widgets ?? [], registry);
            for (const block of section.blocks ?? []) {
                if (block.type === 'image') {
                    validateAsset(block.imageAsset, collection.id);
                }
                visitBlock(block, (nodes) => validateRichTextReferences(collection.id, nodes, registry));
            }
        }
    }
};
export const buildContentRegistry = () => {
    const registry = {
        documents,
        collections,
        documentsById: new Map(documents.map((document) => [document.id, document])),
        documentsByRoute: new Map(documents.map((document) => [document.route, document])),
        collectionsById: new Map(collections.map((collection) => [collection.id, collection])),
        collectionsByRoute: new Map(collections.map((collection) => [collection.route, collection])),
    };
    validateRegistry(registry);
    return registry;
};
export const contentRegistry = buildContentRegistry();
export const buildContentPageRoutes = () => [
    ...contentRegistry.collections.map((collection) => ({
        path: collection.route,
        normalizedPath: collection.route,
        pageTitle: collection.seo.title ?? collection.title,
        seo: {
            title: collection.seo.title,
            description: collection.seo.description ?? collection.description ?? '',
            image: collection.seo.imageAsset
                ? ASSETS[collection.seo.imageAsset]
                : undefined,
        },
    })),
    ...contentRegistry.documents.map((document) => ({
        path: document.route,
        normalizedPath: document.route,
        pageTitle: document.seo.title,
        seo: {
            title: document.seo.title,
            description: document.seo.description,
            image: document.seo.imageAsset
                ? ASSETS[document.seo.imageAsset]
                : undefined,
        },
    })),
];
//# sourceMappingURL=buildContentRegistry.js.map